import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Check,
  CheckCheck,
  FileText,
  Loader2,
  Pencil,
  RefreshCw,
  Scale,
  UserRound,
  X,
} from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import type { CategorieDonnee, Conflit, Donnee } from '../lib/types'
import { Carte, Champ, Chargement, Message, Modale, Pastille } from '../composants/base'
import { Apercu, type DocumentDetail } from '../composants/VisionneuseDocument'
import FormulaireDonnee from '../composants/dossier/FormulaireDonnee'
import Conflits from '../composants/dossier/Conflits'
import * as f from '../lib/format'
import { resume } from '../lib/resume'
import { CATEGORIES_DONNEE, PRECISIONS_DATE, SEXES, TYPES_DOCUMENT } from '@doc/shared'

type Analyse = {
  analyse: {
    id: string
    etat: 'EN_ATTENTE' | 'EN_COURS' | 'A_VERIFIER' | 'VALIDEE' | 'REJETEE' | 'ERREUR'
    erreur: string | null
    modele: string | null
    typeDetecte: string | null
    resume: {
      proposees: number
      dejaAuDossier: { categorie: string; libelle: string }[]
      conflits: number
    } | null
    extraction: { titre?: string; dateDocument?: string; remarques?: string } | null
    jetonsEntree: number | null
    jetonsSortie: number | null
    lanceeLe: string
    termineeLe: string | null
  }
  document: {
    id: string
    titre: string
    type: string
    dateDocument: string | null
    precisionDateDocument: string
    mimeType: string | null
    nbPages: number | null
    ocrApplique: boolean
    patientId: string
  }
  patient: {
    id: string
    reference: string
    affichage: string
    nom: string | null
    prenom: string | null
    dateNaissance: string | null
    sexe: string
  }
  identiteProposee: {
    nom: string | null
    prenom: string | null
    dateNaissance: string | null
    sexe: string
    passage: string | null
  } | null
  correspondances: { id: string; reference: string; affichage: string; dateNaissance: string | null }[]
  propositions: Donnee[]
  traitees: Donnee[]
  conflits: Conflit[]
}

const ORDRE: CategorieDonnee[] = [
  'DIAGNOSTIC',
  'TRAITEMENT',
  'ALLERGIE',
  'ANTECEDENT',
  'EVENEMENT_HISTOIRE',
  'DONNEE_SPECIFIQUE',
]

export default function Verification() {
  const { id } = useParams<{ id: string }>()
  const client = useQueryClient()
  const naviguer = useNavigate()
  const [erreur, setErreur] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['analyse', id],
    queryFn: () => api<Analyse>(`/api/analyses/${id}`),
    // Tant que l'analyse tourne, on redemande où elle en est.
    refetchInterval: (requete) => {
      const etat = (requete.state.data as Analyse | undefined)?.analyse.etat
      return etat === 'EN_ATTENTE' || etat === 'EN_COURS' ? 2000 : false
    },
  })

  const { data: documentComplet } = useQuery({
    queryKey: ['document', data?.document.id],
    queryFn: () => api<{ document: DocumentDetail }>(`/api/documents/${data!.document.id}`),
    enabled: Boolean(data?.document.id),
  })

  const rafraichir = () => {
    client.invalidateQueries({ queryKey: ['analyse', id] })
    client.invalidateQueries({ queryKey: ['dossier', data?.patient.id] })
    client.invalidateQueries({ queryKey: ['accueil'] })
  }

  const relancer = useMutation({
    mutationFn: () =>
      api<{ analyse: { id: string } }>(`/api/documents/${data!.document.id}/analyser`, {
        methode: 'POST',
        corps: {},
      }),
    onSuccess: ({ analyse }) => naviguer(`/verification/${analyse.id}`),
    onError: (e) => setErreur(messageErreur(e)),
  })

  const toutValider = useMutation({
    mutationFn: () => api<{ validees: number; enAttenteDeDecision: number }>(
      `/api/analyses/${id}/tout-valider`,
      { methode: 'POST', corps: {} },
    ),
    onSuccess: rafraichir,
    onError: (e) => setErreur(messageErreur(e)),
  })

  if (isLoading || !data) return <Chargement texte="Ouverture de l’analyse…" />

  const { analyse, document, patient, propositions, traitees, conflits } = data
  const enCours = analyse.etat === 'EN_ATTENTE' || analyse.etat === 'EN_COURS'
  const conflitsOuverts = conflits.filter((c) => c.etat === 'OUVERT')
  const validables = propositions.filter(
    (p) => !conflitsOuverts.some((c) => c.proposee.id === p.id),
  )

  return (
    <div className="mx-auto max-w-[1340px] pt-1">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/patients/${patient.id}`} className="bouton-discret px-2">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-[19px] font-semibold tracking-tight">Vérifier avant d’intégrer</h1>
            <p className="text-[13px] text-brume">
              {document.titre} ·{' '}
              <Link to={`/patients/${patient.id}`} className="text-bleu-600 hover:underline">
                {patient.affichage}
              </Link>{' '}
              · {patient.reference}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="bouton-discret"
            onClick={() => relancer.mutate()}
            disabled={relancer.isPending || enCours}
            title="Relancer l’analyse de ce document"
          >
            <RefreshCw className="h-4 w-4" />
            Relancer
          </button>
          {validables.length > 0 && (
            <button
              className="bouton-primaire"
              onClick={() => toutValider.mutate()}
              disabled={toutValider.isPending}
            >
              <CheckCheck className="h-4 w-4" />
              Tout valider ({validables.length})
            </button>
          )}
        </div>
      </div>

      {erreur && (
        <div className="mb-4">
          <Message ton="critique">{erreur}</Message>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.1fr]">
        {/* Le document, à gauche, pour vérifier d'un coup d'œil */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <Carte
            titre="Le document"
            icone={<FileText className="h-[17px] w-[17px] text-bleu-500" />}
            actions={
              document.ocrApplique ? <Pastille>texte reconnu automatiquement</Pastille> : undefined
            }
          >
            <div className="px-5 pb-5">
              {documentComplet ? (
                <Apercu
                  doc={documentComplet.document}
                  url={`/api/documents/${document.id}/fichier`}
                />
              ) : (
                <Chargement />
              )}
            </div>
          </Carte>
        </div>

        {/* Ce que l'analyse propose, à droite */}
        <div className="flex flex-col gap-5">
          {enCours && (
            <Carte>
              <div className="flex items-center gap-3 px-5 py-6 text-[14px] text-ardoise">
                <Loader2 className="h-5 w-5 animate-spin text-bleu-500" />
                Lecture et analyse du document en cours… Cette page se met à jour toute seule.
              </div>
            </Carte>
          )}

          {analyse.etat === 'ERREUR' && (
            <Carte>
              <div className="px-5 py-5">
                <Message ton="critique">
                  <p className="font-medium">L’analyse n’a pas abouti.</p>
                  <p className="mt-1">{analyse.erreur}</p>
                  <p className="mt-1">
                    Le document reste intact dans le dossier, et rien n’a été modifié.
                  </p>
                </Message>
              </div>
            </Carte>
          )}

          {!enCours && analyse.etat !== 'ERREUR' && (
            <>
              <BlocDocument analyse={data} surSucces={rafraichir} />

              {data.identiteProposee && (
                <BlocIdentite analyse={data} surSucces={rafraichir} />
              )}

              {analyse.extraction?.remarques && (
                <Carte titre="Ce que l’analyse signale sans l’extraire">
                  <div className="px-5 pb-5">
                    <Message ton="info">{analyse.extraction.remarques}</Message>
                  </div>
                </Carte>
              )}

              {conflitsOuverts.length > 0 && (
                <Conflits conflits={conflitsOuverts} patientId={patient.id} />
              )}

              {propositions.length === 0 && (
                <Carte titre="Informations proposées" compte={0}>
                  <div className="px-5 pb-5">
                    <Message ton="info">
                      {traitees.length > 0
                        ? 'Tout a été traité : il ne reste rien à vérifier pour ce document.'
                        : 'Aucune information n’a été extraite de ce document.'}
                    </Message>
                  </div>
                </Carte>
              )}

              {ORDRE.map((categorie) => {
                const liste = propositions.filter((p) => p.categorie === categorie)
                if (liste.length === 0) return null
                return (
                  <Carte
                    key={categorie}
                    titre={CATEGORIES_DONNEE[categorie]}
                    compte={liste.length}
                  >
                    <ul className="divide-y divide-trait pb-2">
                      {liste.map((proposition) => (
                        <Proposition
                          key={proposition.id}
                          donnee={proposition}
                          patientId={patient.id}
                          bloquee={conflitsOuverts.some((c) => c.proposee.id === proposition.id)}
                          surSucces={rafraichir}
                        />
                      ))}
                    </ul>
                  </Carte>
                )
              })}

              {analyse.resume && analyse.resume.dejaAuDossier.length > 0 && (
                <Carte titre="Déjà au dossier, non reproposé" compte={analyse.resume.dejaAuDossier.length}>
                  <ul className="px-5 pb-5 text-[13px] text-ardoise">
                    {analyse.resume.dejaAuDossier.map((d, index) => (
                      <li key={`${d.libelle}-${index}`} className="py-0.5">
                        {CATEGORIES_DONNEE[d.categorie as CategorieDonnee] ?? d.categorie} :{' '}
                        {d.libelle}
                      </li>
                    ))}
                  </ul>
                </Carte>
              )}

              {traitees.length > 0 && (
                <Carte titre="Déjà traité pour ce document" compte={traitees.length}>
                  <ul className="px-5 pb-5">
                    {traitees.map((d) => (
                      <li key={d.id} className="flex items-center gap-2 py-1 text-[13px]">
                        {d.etat === 'VALIDEE' ? (
                          <Check className="h-3.5 w-3.5 text-valide" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-brume" />
                        )}
                        <span className={d.etat === 'VALIDEE' ? '' : 'text-brume line-through'}>
                          {d.libelle}
                        </span>
                        {d.motifRejet && (
                          <span className="text-[12px] text-brume">— {d.motifRejet}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </Carte>
              )}

              <p className="px-1 text-[11.5px] text-brume">
                Analyse menée par {analyse.modele ?? 'le modèle'}
                {analyse.termineeLe ? ` le ${f.heure(analyse.termineeLe)}` : ''}
                {analyse.jetonsEntree
                  ? ` · ${analyse.jetonsEntree.toLocaleString('fr-FR')} jetons lus, ${analyse.jetonsSortie?.toLocaleString('fr-FR')} écrits (environ ${(
                      ((analyse.jetonsEntree * 5 + (analyse.jetonsSortie ?? 0) * 25) / 1_000_000) *
                      0.92
                    )
                      .toFixed(2)
                      .replace('.', ',')} €)`
                  : ''}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function BlocDocument({ analyse, surSucces }: { analyse: Analyse; surSucces: () => void }) {
  const propose = analyse.analyse.extraction
  const [titre, setTitre] = useState(propose?.titre || analyse.document.titre)
  const [type, setType] = useState(analyse.analyse.typeDetecte ?? analyse.document.type)
  const [date, setDate] = useState(
    f.pourChampDate(propose?.dateDocument || analyse.document.dateDocument),
  )
  const [precision, setPrecision] = useState(analyse.document.precisionDateDocument)
  const [fait, setFait] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const appliquer = useMutation({
    mutationFn: () =>
      api(`/api/analyses/${analyse.analyse.id}/document`, {
        methode: 'POST',
        corps: { titre, type, dateDocument: date || null, precisionDateDocument: precision },
      }),
    onSuccess: () => {
      setFait(true)
      surSucces()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Carte titre="Le document lui-même">
      <div className="grid grid-cols-1 gap-3 px-5 pb-5 sm:grid-cols-2">
        <Champ etiquette="Titre" className="sm:col-span-2">
          <input className="champ" value={titre} onChange={(e) => setTitre(e.target.value)} />
        </Champ>
        <Champ etiquette="Type de document">
          <select className="champ" value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(TYPES_DOCUMENT).map(([cle, libelle]) => (
              <option key={cle} value={cle}>
                {libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ etiquette="Date du document">
          <input
            className="champ"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Champ>
        <Champ etiquette="Précision de la date">
          <select
            className="champ"
            value={precision}
            onChange={(e) => setPrecision(e.target.value)}
          >
            {Object.entries(PRECISIONS_DATE).map(([cle, libelle]) => (
              <option key={cle} value={cle}>
                {libelle}
              </option>
            ))}
          </select>
        </Champ>

        <div className="flex items-center gap-3 sm:col-span-2">
          <button
            className="bouton-doux"
            onClick={() => appliquer.mutate()}
            disabled={appliquer.isPending}
          >
            <Check className="h-4 w-4" />
            Enregistrer sur le document
          </button>
          {fait && <span className="text-[13px] text-valide">Enregistré</span>}
        </div>
        {erreur && (
          <div className="sm:col-span-2">
            <Message ton="critique">{erreur}</Message>
          </div>
        )}
      </div>
    </Carte>
  )
}

function BlocIdentite({ analyse, surSucces }: { analyse: Analyse; surSucces: () => void }) {
  const propose = analyse.identiteProposee!
  const actuel = analyse.patient
  const [choix, setChoix] = useState<Record<string, boolean>>({
    nom: Boolean(propose.nom) && !actuel.nom,
    prenom: Boolean(propose.prenom) && !actuel.prenom,
    dateNaissance: Boolean(propose.dateNaissance) && !actuel.dateNaissance,
    sexe: propose.sexe !== 'NON_RENSEIGNE' && actuel.sexe === 'NON_RENSEIGNE',
  })
  const [fait, setFait] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const appliquer = useMutation({
    mutationFn: () => {
      const corps: Record<string, unknown> = {}
      if (choix.nom) corps.nom = propose.nom
      if (choix.prenom) corps.prenom = propose.prenom
      if (choix.dateNaissance) corps.dateNaissance = propose.dateNaissance
      if (choix.sexe) corps.sexe = propose.sexe
      return api(`/api/analyses/${analyse.analyse.id}/identite`, { methode: 'POST', corps })
    },
    onSuccess: () => {
      setFait(true)
      surSucces()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const lignes = [
    { cle: 'nom', etiquette: 'Nom', propose: propose.nom, actuel: actuel.nom },
    { cle: 'prenom', etiquette: 'Prénom', propose: propose.prenom, actuel: actuel.prenom },
    {
      cle: 'dateNaissance',
      etiquette: 'Date de naissance',
      propose: propose.dateNaissance ? f.dateCourte(propose.dateNaissance) : null,
      actuel: actuel.dateNaissance ? f.dateCourte(actuel.dateNaissance) : null,
    },
    {
      cle: 'sexe',
      etiquette: 'Sexe',
      propose: SEXES[propose.sexe as keyof typeof SEXES],
      actuel: SEXES[actuel.sexe as keyof typeof SEXES],
    },
  ].filter((ligne) => ligne.propose)

  const rienAFaire = lignes.every((l) => l.propose === l.actuel)

  return (
    <Carte
      titre="La personne concernée"
      icone={<UserRound className="h-[17px] w-[17px] text-bleu-500" />}
    >
      <div className="px-5 pb-5">
        {analyse.correspondances.length > 0 && (
          <div className="mb-3">
            <Message ton="attention">
              <p className="font-medium">
                Un autre dossier porte déjà cette identité. Il s’agit peut-être de la même personne.
              </p>
              <ul className="mt-1">
                {analyse.correspondances.map((c) => (
                  <li key={c.id}>
                    <Link to={`/patients/${c.id}`} className="text-bleu-700 underline">
                      {c.affichage} — {c.reference}
                    </Link>
                    {c.dateNaissance ? ` · née le ${f.dateCourte(c.dateNaissance)}` : ''}
                  </li>
                ))}
              </ul>
              <p className="mt-1">
                Si c’est bien elle, ouvre son dossier et utilise « Fusionner » : rien ne sera perdu.
              </p>
            </Message>
          </div>
        )}

        {propose.passage && (
          <p className="mb-3 text-[12.5px] text-brume italic">
            Lu dans le document : « {propose.passage} »
          </p>
        )}

        {rienAFaire ? (
          <p className="text-[13px] text-ardoise">
            L’identité du document correspond déjà à celle du dossier.
          </p>
        ) : (
          <>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11.5px] tracking-wide text-brume uppercase">
                  <th className="pb-1 font-medium">Champ</th>
                  <th className="pb-1 font-medium">Au dossier</th>
                  <th className="pb-1 font-medium">Dans le document</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => (
                  <tr key={ligne.cle} className="border-t border-trait">
                    <td className="py-2 text-brume">{ligne.etiquette}</td>
                    <td className="py-2">{ligne.actuel ?? '—'}</td>
                    <td className="py-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[var(--color-bleu-600)]"
                          checked={Boolean(choix[ligne.cle])}
                          onChange={(e) =>
                            setChoix((c) => ({ ...c, [ligne.cle]: e.target.checked }))
                          }
                        />
                        {ligne.propose}
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-3 flex items-center gap-3">
              <button
                className="bouton-doux"
                onClick={() => appliquer.mutate()}
                disabled={appliquer.isPending || !Object.values(choix).some(Boolean)}
              >
                <Check className="h-4 w-4" />
                Appliquer au dossier
              </button>
              {fait && <span className="text-[13px] text-valide">Identité enregistrée</span>}
            </div>
          </>
        )}

        {erreur && (
          <div className="mt-3">
            <Message ton="critique">{erreur}</Message>
          </div>
        )}
      </div>
    </Carte>
  )
}

function Proposition({
  donnee,
  patientId,
  bloquee,
  surSucces,
}: {
  donnee: Donnee
  patientId: string
  bloquee: boolean
  surSucces: () => void
}) {
  const [edition, setEdition] = useState(false)
  const [rejet, setRejet] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const action = useMutation({
    mutationFn: (params: { quoi: 'valider' | 'rejeter'; motif?: string }) =>
      api(`/api/donnees/${donnee.id}/${params.quoi}`, {
        methode: 'POST',
        corps: params.motif ? { motif: params.motif } : {},
      }),
    onSuccess: surSucces,
    onError: (e) => setErreur(messageErreur(e)),
  })

  const source = donnee.sources[0]
  const detail = resume(donnee)

  return (
    <li className="px-5 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium">{donnee.libelle}</p>
          {detail && <p className="mt-0.5 text-[12.5px] text-ardoise">{detail}</p>}
          {source?.passage && (
            <p className="mt-1 border-l-2 border-bleu-100 pl-2.5 text-[12.5px] text-brume italic">
              « {source.passage} »
              {source.page ? <span className="not-italic"> — page {source.page}</span> : null}
            </p>
          )}
          {bloquee && (
            <p className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-critique">
              <Scale className="h-3.5 w-3.5" />
              Contredit une information du dossier : à trancher plus haut avant de valider.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <button
            className="bouton-doux px-2.5 py-1.5"
            onClick={() => action.mutate({ quoi: 'valider' })}
            disabled={action.isPending || bloquee}
            title={bloquee ? 'Tranche d’abord le désaccord' : 'Valider et intégrer au dossier'}
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            className="bouton-discret px-2"
            onClick={() => setEdition(true)}
            title="Corriger avant de valider"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            className="bouton-discret px-2 text-critique"
            onClick={() => setRejet(true)}
            title="Rejeter"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {erreur && (
        <div className="mt-2">
          <Message ton="critique">{erreur}</Message>
        </div>
      )}

      {edition && (
        <FormulaireDonnee
          patientId={patientId}
          categorie={donnee.categorie}
          donnee={donnee}
          fermer={() => {
            setEdition(false)
            surSucces()
          }}
        />
      )}

      {rejet && (
        <Rejet
          libelle={donnee.libelle}
          fermer={() => setRejet(false)}
          rejeter={(motif) => {
            action.mutate({ quoi: 'rejeter', motif })
            setRejet(false)
          }}
        />
      )}
    </li>
  )
}

function Rejet({
  libelle,
  fermer,
  rejeter,
}: {
  libelle: string
  fermer: () => void
  rejeter: (motif: string) => void
}) {
  const [motif, setMotif] = useState('')
  // Le motif est facultatif, mais il reste consultable avec la proposition.
  useEffect(() => setMotif(''), [libelle])

  return (
    <Modale
      titre="Rejeter cette proposition"
      sousTitre={libelle}
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button className="bouton-primaire" onClick={() => rejeter(motif)}>
            Rejeter
          </button>
        </>
      }
    >
      <Message ton="info">
        La proposition n’est pas effacée : elle reste consultable avec son motif, et le document
        garde sa trace.
      </Message>
      <div className="mt-3">
        <Champ etiquette="Motif (facultatif)">
          <input
            className="champ"
            autoFocus
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder="Ne concerne pas cette patiente, doublon, lecture erronée…"
          />
        </Champ>
      </div>
    </Modale>
  )
}
